import React, { useState } from 'react'
import { defaultImage } from '@assets'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text, SelectionIndicator } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import type { ScrapedProfile } from '@hooks'

interface ScrapedProfileItemProps {
  profile: ScrapedProfile
  isSelected: boolean
  onToggle: () => void
  disabled?: boolean
}

export const ScrapedProfileItem = ({ profile, isSelected, onToggle, disabled = false }: ScrapedProfileItemProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [imageError, setImageError] = useState(false)

  const displayName = profile.fullName || [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.username || 'Unknown'

  return (
    <TouchableOpacity
      style={[styles.container, disabled && styles.disabled]}
      onPress={disabled ? undefined : onToggle}
      activeOpacity={disabled ? 1 : 0.7}
      disabled={disabled}
    >
      <View style={styles.avatarContainer}>
        {profile.avatarUrl && !imageError ? (
          <Image
            source={profile.avatarUrl ? { uri: profile.avatarUrl } : defaultImage}
            style={styles.avatar}
            onError={() => setImageError(true)}
          />
        ) : (
          <View style={[styles.avatar, styles.avatarPlaceholder]}>
            <Text type="body" color={theme.colors.white}>
              {displayName.charAt(0).toUpperCase()}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.info}>
        <Text type="body" color={theme.colors.black} numberOfLines={1}>
          {displayName}
        </Text>
        {profile.username && (
          <Text type="bodyExtraSmall" color={theme.colors.secondary} numberOfLines={1}>
            @{profile.username}
          </Text>
        )}
        {profile.bio && (
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey} numberOfLines={2}>
            {profile.bio}
          </Text>
        )}
      </View>
      {!disabled && <SelectionIndicator isSelected={isSelected} />}
      {disabled && (
        <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
          {t('ftSocialImport.alreadyAdded')}
        </Text>
      )}
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      gap: theme.spacing.md,
    },
    disabled: {
      opacity: 0.4,
    },
    avatarContainer: {
      width: 48,
      height: 48,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
    },
    avatarPlaceholder: {
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    info: {
      flex: 1,
      gap: 2,
    },
  })
